import { useCallback, useEffect, useRef, useState } from 'react';
import { DraggableData, Rnd, RndResizeCallback, Props as RndProps } from 'react-rnd';

type UseCustomRndProps = {
  width: number;
  height: number;
  useDynamicPositioning?: boolean;
};

const useCustomRnd = ({ width, height, useDynamicPositioning }: UseCustomRndProps): Partial<RndProps> => {
  const ref = useRef<Rnd>();
  // Initialize centered position
  const [size, setSize] = useState({ width, height });

  // Keeping track of the position
  const [position, setPosition] = useState({
    x: window.innerWidth / 2 - width / 2,
    y: window.innerHeight / 2 - height / 2,
  });

  // Calculate center position with updated dimensions
  const centerWindowIfNeeded = useCallback(() => {
    if (window.innerWidth > size.width) {
      const newPosition = {
        x: (window.innerWidth - size.width) / 2,
        y: (window.innerHeight - height) / 2,
      };

      // Found a weird behavior when trying to set this trough props (it was adding 200px (even when bounds = 'windows'))
      // This was the straightforward solution
      ref.current?.updatePosition(newPosition);
      setPosition(newPosition);
    }
  }, [height, size.width]);

  const adjustToWindowIfNeeded = useCallback(() => {
    if (window.innerWidth < size.width) {
      setSize((s) => ({ ...s, width: window.innerWidth }));
      ref.current?.updatePosition({ y: position.y, x: 0 });
    }
  }, [position.y, size.width]);

  useEffect(() => {
    const handleWindowResize = (): void => {
      centerWindowIfNeeded();
      adjustToWindowIfNeeded();
    };

    // Add event listener for window resize
    if (useDynamicPositioning) {
      window.addEventListener('resize', handleWindowResize);
    }

    // Cleanup event listener on component unmount
    return (): void => {
      if (useDynamicPositioning) {
        window.removeEventListener('resize', handleWindowResize);
      }
    };
  }, [useDynamicPositioning, centerWindowIfNeeded, adjustToWindowIfNeeded]);

  useEffect(() => {
    if (useDynamicPositioning) {
      centerWindowIfNeeded();
      adjustToWindowIfNeeded();
    }
    // Almost empty dependency array ensures this effect runs only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDynamicPositioning]);

  const onResizeStop: RndResizeCallback = (e, direction, ref) => {
    const updatedSize = {
      width: parseInt(ref.style.width, 10),
      height: parseInt(ref.style.height, 10),
    };
    setSize(updatedSize);
    // Position is automatically updated by Rnd when resizing, no need to adjust here unless centering is desired
  };

  if (useDynamicPositioning) {
    return {
      ref,
      size,
      onDragStop: (e: unknown, d: DraggableData): void => {
        setPosition({ x: d.x, y: d.y });
      },
      onResizeStop,
    };
  }

  return {};
};

export default useCustomRnd;
